/**
 * Error Logger Service for Notification System
 *
 * Provides comprehensive error logging and reporting functionality
 */

// Error severity levels
export const SEVERITY_LEVELS = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  CRITICAL: "critical",
};

// Error categories
export const ERROR_CATEGORIES = {
  API: "api",
  COMPONENT: "component",
  NETWORK: "network",
  AUTH: "auth",
  VALIDATION: "validation",
  PERFORMANCE: "performance",
  USER_ACTION: "user_action",
};

/**
 * Error Logger Class
 */
class ErrorLogger {
  constructor() {
    this.logs = [];
    this.maxLogs = 1000; // Keep last 1000 logs in memory
    this.sessionId = this.generateSessionId();
    this.userId = null;
    this.isEnabled = true;
  }

  /**
   * Generate unique session ID
   */
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Set user ID for error tracking
   */
  setUserId(userId) {
    this.userId = userId;
  }

  /**
   * Enable or disable error logging
   */
  setEnabled(enabled) {
    this.isEnabled = enabled;
  }

  /**
   * Log an error with comprehensive context
   */
  logError(error, context = {}) {
    if (!this.isEnabled) return;

    const errorLog = this.createErrorLog(error, context);

    // Add to in-memory logs
    this.addToMemoryLogs(errorLog);

    // Log to console in development
    if (process.env.NODE_ENV === "development") {
      this.logToConsole(errorLog);
    }

    // Send to external service in production
    if (process.env.NODE_ENV === "production") {
      this.sendToExternalService(errorLog);
    }

    // Store in local storage for offline scenarios
    this.storeLocally(errorLog);

    return errorLog;
  }

  /**
   * Create structured error log object
   */
  createErrorLog(error, context) {
    const timestamp = new Date().toISOString();
    const errorId = this.generateErrorId();

    return {
      id: errorId,
      timestamp,
      sessionId: this.sessionId,
      userId: this.userId,

      // Error details
      message: error.message || "Unknown error",
      stack: error.stack,
      name: error.name,
      type: error.type || "UNKNOWN_ERROR",

      // HTTP details (if applicable)
      status: error.response?.status,
      statusText: error.response?.statusText,
      requestUrl: error.config?.url || error.request?.responseURL,
      method: error.config?.method,

      // Browser context
      userAgent: navigator.userAgent,
      url: window.location.href,
      referrer: document.referrer,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },

      // Application context
      component: context.component,
      action: context.action,
      category: context.category || ERROR_CATEGORIES.API,
      severity: context.severity || this.determineSeverity(error),

      // Additional context
      metadata: {
        ...context.metadata,
        retryCount: context.retryCount || 0,
        timeToError: context.timeToError,
        memoryUsage: this.getMemoryUsage(),
        connectionType: this.getConnectionType(),
      },
    };
  }

  /**
   * Generate unique error ID
   */
  generateErrorId() {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Determine error severity based on error type and context
   */
  determineSeverity(error) {
    if (error.response?.status >= 500) return SEVERITY_LEVELS.HIGH;
    if (error.response?.status === 401) return SEVERITY_LEVELS.MEDIUM;
    if (error.response?.status === 404) return SEVERITY_LEVELS.LOW;
    if (error.type === "NETWORK_ERROR") return SEVERITY_LEVELS.MEDIUM;
    if (error.type === "TIMEOUT_ERROR") return SEVERITY_LEVELS.LOW;
    return SEVERITY_LEVELS.MEDIUM;
  }

  /**
   * Get memory usage information
   */
  getMemoryUsage() {
    if (performance.memory) {
      return {
        used: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024),
        total: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024),
        limit: Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024),
      };
    }
    return null;
  }

  /**
   * Get connection type information
   */
  getConnectionType() {
    if (navigator.connection) {
      return {
        effectiveType: navigator.connection.effectiveType,
        downlink: navigator.connection.downlink,
        rtt: navigator.connection.rtt,
      };
    }
    return null;
  }

  /**
   * Add error log to in-memory storage
   */
  addToMemoryLogs(errorLog) {
    this.logs.push(errorLog);

    // Keep only the last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }
  }

  /**
   * Log error to console with formatting
   */
  logToConsole(errorLog) {
    const { severity, category, component, message, stack } = errorLog;

    console.group(`🚨 Notification Error [${severity.toUpperCase()}]`);
    console.log(`Category: ${category}`);
    console.log(`Component: ${component || "Unknown"}`);
    console.log(`Message: ${message}`);
    console.log(`Timestamp: ${errorLog.timestamp}`);
    console.log(`Error ID: ${errorLog.id}`);

    if (stack) {
      console.log(`Stack Trace:`, stack);
    }

    console.log(`Full Error Log:`, errorLog);
    console.groupEnd();
  }

  /**
   * Send error log to external service
   */
  async sendToExternalService(errorLog) {
    try {
      // Example: Send to error reporting service
      // Replace with your actual error reporting service

      // Sentry example:
      // if (window.Sentry) {
      //   window.Sentry.captureException(new Error(errorLog.message), {
      //     tags: {
      //       component: errorLog.component,
      //       category: errorLog.category,
      //       severity: errorLog.severity,
      //     },
      //     extra: errorLog,
      //   });
      // }

      // LogRocket example:
      // if (window.LogRocket) {
      //   window.LogRocket.captureException(new Error(errorLog.message));
      // }

      // Custom API endpoint example:
      // await fetch('/api/errors', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(errorLog),
      // });

      console.log("Error log would be sent to external service:", errorLog.id);
    } catch (sendError) {
      console.error("Failed to send error log to external service:", sendError);
    }
  }

  /**
   * Store error log locally for offline scenarios
   */
  storeLocally(errorLog) {
    try {
      const key = `notification_error_${errorLog.id}`;
      const storedLogs = this.getStoredLogs();

      storedLogs.push({
        id: errorLog.id,
        timestamp: errorLog.timestamp,
        severity: errorLog.severity,
        message: errorLog.message,
        component: errorLog.component,
      });

      // Keep only last 50 logs in localStorage
      const recentLogs = storedLogs.slice(-50);

      localStorage.setItem(
        "notification_error_logs",
        JSON.stringify(recentLogs)
      );
      localStorage.setItem(key, JSON.stringify(errorLog));
    } catch (storageError) {
      console.error("Failed to store error log locally:", storageError);
    }
  }

  /**
   * Get stored error logs from localStorage
   */
  getStoredLogs() {
    try {
      const stored = localStorage.getItem("notification_error_logs");
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error("Failed to retrieve stored error logs:", error);
      return [];
    }
  }

  /**
   * Clear stored error logs
   */
  clearStoredLogs() {
    try {
      const storedLogs = this.getStoredLogs();

      // Remove individual error log entries
      storedLogs.forEach((log) => {
        localStorage.removeItem(`notification_error_${log.id}`);
      });

      // Remove the index
      localStorage.removeItem("notification_error_logs");
    } catch (error) {
      console.error("Failed to clear stored error logs:", error);
    }
  }

  /**
   * Get error statistics
   */
  getErrorStats() {
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;
    const oneDay = 24 * oneHour;

    const recentLogs = this.logs.filter(
      (log) => now - new Date(log.timestamp).getTime() < oneDay
    );

    const hourlyLogs = this.logs.filter(
      (log) => now - new Date(log.timestamp).getTime() < oneHour
    );

    const severityCount = recentLogs.reduce((acc, log) => {
      acc[log.severity] = (acc[log.severity] || 0) + 1;
      return acc;
    }, {});

    const categoryCount = recentLogs.reduce((acc, log) => {
      acc[log.category] = (acc[log.category] || 0) + 1;
      return acc;
    }, {});

    return {
      total: this.logs.length,
      last24Hours: recentLogs.length,
      lastHour: hourlyLogs.length,
      bySeverity: severityCount,
      byCategory: categoryCount,
      sessionId: this.sessionId,
    };
  }

  /**
   * Export error logs for debugging
   */
  exportLogs(format = "json") {
    const stats = this.getErrorStats();
    const exportData = {
      metadata: {
        exportedAt: new Date().toISOString(),
        sessionId: this.sessionId,
        userId: this.userId,
        stats,
      },
      logs: this.logs,
    };

    if (format === "json") {
      return JSON.stringify(exportData, null, 2);
    }

    if (format === "csv") {
      const headers = [
        "timestamp",
        "severity",
        "category",
        "component",
        "message",
        "type",
      ];
      const csvRows = [headers.join(",")];

      this.logs.forEach((log) => {
        const row = headers.map((header) => {
          const value = log[header] || "";
          return `"${value.toString().replace(/"/g, '""')}"`;
        });
        csvRows.push(row.join(","));
      });

      return csvRows.join("\n");
    }

    return exportData;
  }
}

// Create singleton instance
const errorLogger = new ErrorLogger();

// Export convenience functions
export const logError = (error, context) =>
  errorLogger.logError(error, context);
export const setUserId = (userId) => errorLogger.setUserId(userId);
export const setEnabled = (enabled) => errorLogger.setEnabled(enabled);
export const getErrorStats = () => errorLogger.getErrorStats();
export const exportLogs = (format) => errorLogger.exportLogs(format);
export const clearStoredLogs = () => errorLogger.clearStoredLogs();

export default errorLogger;
